import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate, DURUM_COLORS } from "@/lib/format";
import ColumnFilter from "@/components/ColumnFilter";
import { useColFilter } from "@/lib/tableFilter";

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

const PERIODS = [
  { key: "today", label: "Bugün" },
  { key: "week", label: "Bu Hafta" },
  { key: "month", label: "Bu Ay" },
  { key: "overdue", label: "Gecikmiş" },
  { key: "all", label: "Tümü" },
];

function inPeriod(r, period) {
  const gun = r.gun;
  if (period === "today") return gun === 0;
  if (period === "week") return gun >= 0 && gun <= 7;
  if (period === "month") {
    // Yalnızca içinde bulunulan TAKVİM AYI (devreden yok)
    const now = new Date();
    const d = new Date(r.vade_tarihi);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }
  if (period === "overdue") return gun < 0;
  return true;
}

export default function CariVadeler() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cari").then((res) => setRecords(res.data)).finally(() => setLoading(false));
  }, []);

  const withVade = records
    .filter((r) => r.vade_tarihi && r.kalan > 0)
    .map((r) => ({ ...r, gun: daysUntil(r.vade_tarihi) }))
    .sort((a, b) => a.gun - b.gun);

  const rows = withVade.filter((r) => inPeriod(r, period));
  const CVCOLS = [
    { key: "musteri", label: "Müşteri", type: "text" },
    { key: "vade_tarihi", label: "Vade Tarihi", type: "date" },
    { key: "tutar", label: "Tutar", type: "money" },
    { key: "kalan", label: "Kalan", type: "money" },
    { key: "durum", label: "Durum", type: "text" },
  ];
  const { colSel, setColSel, sort, setSort, view, distinct } = useColFilter(CVCOLS, rows);
  const toplamKalan = view.reduce((s, r) => s + (r.kalan || 0), 0);
  const toplamTutar = view.reduce((s, r) => s + (r.tutar || 0), 0);
  const devreden = view.filter((r) => r.gun < 0).reduce((s, r) => s + (r.kalan || 0), 0);

  const statusBadge = (gun) => {
    if (gun < 0) return <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">{Math.abs(gun)} gün gecikmiş</span>;
    if (gun === 0) return <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">Bugün</span>;
    if (gun <= 30) return <span className="px-2 py-0.5 rounded-sm bg-[#B8933F]/10 text-[#B8933F] text-xs font-semibold">{gun} gün kaldı</span>;
    return <span className="px-2 py-0.5 rounded-sm bg-[#EFE7D6] text-[#8C8377] text-xs font-semibold">{gun} gün</span>;
  };

  return (
    <Layout>
      <div className="kicker mb-1">CARİ TAKİP</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-4">Cari Vadeleri</h1>

      {/* Dönem filtresi */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cari-vade-filters">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`cari-vade-chip-${p.key}`}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${
              period === p.key ? "bg-[#B8933F] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Toplam özeti */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-vade-adet">
          <div className="kicker mb-1.5">Taksit Sayısı</div>
          <div className="font-heading font-bold text-lg tabular-nums">{view.length}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-vade-toplam-tutar">
          <div className="kicker mb-1.5">Toplam Taksit Tutarı</div>
          <div className="font-heading font-bold text-lg tabular-nums">{fmt(toplamTutar)}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-vade-toplam-kalan">
          <div className="kicker mb-1.5">Toplam Kalan</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#A63A3A]">{fmt(toplamKalan)}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-vade-devreden">
          <div className="kicker mb-1.5">Devreden Gecikme</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#B8933F]">{fmt(devreden)}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8C8377] text-sm">Yükleniyor...</div>
      ) : (
        <div className="bg-white border border-[#E7DECC] rounded-sm" data-testid="cari-vade-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                  {CVCOLS.map((c) => (
                    <th key={c.key} className={`px-3 py-2.5 kicker ${c.type === "money" ? "text-right" : "text-left"}`}>
                      <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                        <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                        <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-2.5 kicker text-left">Kalan Süre</th>
                </tr>
              </thead>
              <tbody>
                {view.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-[#8C8377]" data-testid="cari-vade-empty">Bu dönemde vadesi gelen taksit yok.</td></tr>
                ) : (
                  view.map((r) => {
                    const dc = DURUM_COLORS[r.durum] || "#8C8377";
                    return (
                      <tr key={r.id} className="border-b border-[#E7DECC] hover:bg-[#EFE7D6] cursor-pointer transition-colors duration-150" onClick={() => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)} data-testid={`cari-vade-row-${r.id}`}>
                        <td className="px-3 py-2 font-medium">{r.musteri}</td>
                        <td className="px-3 py-2 tabular-nums">{fmtDate(r.vade_tarihi)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(r.tutar)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(r.kalan)}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${dc}1a`, color: dc }}>{r.durum}</span></td>
                        <td className="px-3 py-2">{statusBadge(r.gun)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {view.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                    <td className="px-3 py-2.5" colSpan={2}>TOPLAM ({view.length})</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(toplamTutar)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-[#A63A3A]">{fmt(toplamKalan)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
